import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { useTheme } from 'styled-components';
import { displayUserAvatar } from '../../Helpers/chatHelper/AvatarHelper';
import { useAuthService } from '../../Context/AuthContext';
import { useTranslate } from '@tolgee/react';


const ReactionsContainer = ({
	id,
	reactions,
	participants,
	adjustmentForSide,
	handleRemoveOwnMessageReaction,
	conversationId,
	messageId,
}) => {
	const ref = useRef();
	const containerRef = useRef();
	const menuRef = useRef();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [position, setPosition] = useState({ side: 'right', vertical: 'bottom' });
	const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
	const theme = useTheme();
	const { user } = useAuthService();
	const [selectedTab, setSelectedTab] = useState('ALL');
	const { t } = useTranslate();
	
	const calculateOptimalPosition = () => {
		if (!containerRef.current) return;
		
		const reactionsButton = containerRef.current;
		const rect = reactionsButton.getBoundingClientRect();
		const menuWidth = 250; // TabsContainer width
		
		// Calculate actual menu height based on content
		const tabsHeight = 40; // Height of tabs section
		const reactionUserHeight = 62; // Approximate height per reaction user (42px avatar + padding)
		const padding = 20; // Top and bottom padding
		const filteredReactions = reactions.filter((reaction) =>
			selectedTab === 'ALL' || reaction.reaction === selectedTab
		);
		const actualMenuHeight = tabsHeight + (filteredReactions.length * reactionUserHeight) + padding;
		const maxMenuHeight = 300; // Max height from CSS
		const menuHeight = Math.min(actualMenuHeight, maxMenuHeight);
		
		const viewport = {
			width: window.innerWidth,
			height: window.innerHeight
		};
		
		// Calculate available space on each side
		const spaceRight = viewport.width - rect.right;
		const spaceLeft = rect.left;
		const spaceBottom = viewport.height - rect.bottom;
		const spaceTop = rect.top;
		
		// Determine horizontal position
		let horizontalSide = 'right';
		let left = rect.right + 8;
		
		if (spaceRight >= menuWidth) {
			horizontalSide = 'right';
			left = rect.right + 8;
		} else if (spaceLeft >= menuWidth) {
			horizontalSide = 'left';
			left = rect.left - menuWidth - 8;
		} else {
			// Choose side with more space
			if (spaceRight > spaceLeft) {
				horizontalSide = 'right';
				left = rect.right + 8;
			} else {
				horizontalSide = 'left';
				left = rect.left - menuWidth - 8;
			}
		}
		
		// Determine vertical position
		let verticalSide = 'bottom';
		let top = rect.top;
		
		if (spaceBottom >= menuHeight) {
			verticalSide = 'bottom';
			top = rect.top;
		} else if (spaceTop >= menuHeight) {
			verticalSide = 'top';
			top = rect.bottom - menuHeight;
		} else {
			// Choose side with more space and adjust position to fit
			if (spaceBottom > spaceTop) {
				verticalSide = 'bottom';
				top = Math.max(0, viewport.height - menuHeight - 10); // 10px margin from bottom
			} else {
				verticalSide = 'top';
				top = Math.max(10, rect.top - menuHeight); // 10px margin from top
			}
		}
		
		setPosition({ side: horizontalSide, vertical: verticalSide });
		setPortalPosition({ top, left });
	};
	
	const handleOpen = () => {
		setIsMenuOpen((prev) => !prev);
	};

	useEffect(() => {
		const checkIfClickedOutside = (e) => {
			if (isMenuOpen && ref.current && !ref.current.contains(e.target)) {
				// Also check if click is inside the portal content
				const portalElements = document.querySelectorAll('[data-reactions-portal]');
				let clickedInsidePortal = false;
				portalElements.forEach(element => {
					if (element.contains(e.target)) {
						clickedInsidePortal = true;
					}
				});
				
				if (!clickedInsidePortal) {
					setIsMenuOpen(false);
				}
			}
		};
		document.addEventListener('mousedown', checkIfClickedOutside);
		return () => {
			document.removeEventListener('mousedown', checkIfClickedOutside);
		};
	}, [isMenuOpen]);

	// Calculate position after menu opens and DOM is ready
	useEffect(() => {
		if (isMenuOpen && containerRef.current) {
			// Calculate immediately and also with a small delay as backup
			calculateOptimalPosition();
			const timer = setTimeout(() => {
				calculateOptimalPosition();
			}, 10);
			return () => clearTimeout(timer);
		}
	}, [isMenuOpen]);

	useEffect(() => {
		const handleResize = () => {
			if (isMenuOpen) {
				calculateOptimalPosition();
			}
		};

		const handleScroll = () => {
			if (isMenuOpen) {
				calculateOptimalPosition();
			}
		};

		if (isMenuOpen) {
			window.addEventListener('resize', handleResize);
			window.addEventListener('scroll', handleScroll, true);
			return () => {
				window.removeEventListener('resize', handleResize);
				window.removeEventListener('scroll', handleScroll, true);
			};
		}
	}, [isMenuOpen, selectedTab]);

	const uniqueReactions = [...new Set(reactions.map((reaction) => reaction.reaction))];

	return (
		<Container ref={ref} id={id}>
			<Reactions
				ref={containerRef}
				theme={theme}
				onClick={participants && participants.length > 0 ? handleOpen : undefined}
				adjustmentForSide={adjustmentForSide}
				reactions={uniqueReactions.length}
			>
				{uniqueReactions.slice(0, 2).map((reaction, i) => (
					<span key={i}>{reaction}</span>
				))}
				{reactions.length >= 2 && reactions.length}
			</Reactions>
			{isMenuOpen && portalPosition.top >= 0 && portalPosition.left >= 0 && createPortal(
				<TabsContainer ref={menuRef} portalPosition={portalPosition} theme={theme} data-reactions-portal>
					<Tabs theme={theme}>
						<Tab onClick={() => setSelectedTab('ALL')}>{t('reactions.tabs_all')} {reactions.length}</Tab>
						{uniqueReactions.map((reaction) => (
							<Tab key={reaction} onClick={() => setSelectedTab(reaction)}>
								{reaction} {reactions.filter((r) => r.reaction === reaction).length}
							</Tab>
						))}
					</Tabs>
					<TabPanel theme={theme}>
						{reactions
							.filter((reaction) => selectedTab === 'ALL' || reaction.reaction === selectedTab)
							.map((reaction) => {
								const participant = participants.find((p) => p.id === reaction.user_id);
								const isOwnUser = user.id === reaction.user_id;
								return (
									<ReactionUser
										key={reaction.user_id}
										theme={theme}
										onClick={
										isOwnUser
											? () => handleRemoveOwnMessageReaction(reaction.id, conversationId, messageId)
											: undefined
										}
									>
										<Avatar>{displayUserAvatar(isOwnUser ? user : participant)}</Avatar>
										<MyReaction theme={theme}>
											<span>{isOwnUser ? user.name : participant?.name}</span>
											{isOwnUser && <span className="removeMyReaction">{t('reactions.remove')}</span>}
										</MyReaction>
										<span>{reaction.reaction}</span>
									</ReactionUser>
								);
							})}
					</TabPanel>
				</TabsContainer>,
				document.body
			)}
		</Container>
	);
};

export default ReactionsContainer;

const Container = styled.div``;

const Reactions = styled.div`
	position: absolute;
	width: ${(props) => (props.reactions >= 2 ? '70px' : '45px')};
	height: 25px;
	left: ${(props) => (props.adjustmentForSide ? '-35px' : 'unset')};
	right: ${(props) => (props.adjustmentForSide ? 'unset' : '-16px')};
	bottom: -8px;
	border-radius: 100px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme }) => theme.chat.conversationHeader};
	cursor: pointer;
`;

const TabsContainer = styled.div`
	position: fixed;
	width: 250px;
	max-height: 300px;
	z-index: 999999;
	border-radius: 6px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	background: ${({ theme }) => theme.menu.background};
	top: ${(props) => props.portalPosition.top}px;
	left: ${(props) => props.portalPosition.left}px;
`;

const Tabs = styled.div`
	display: flex;
	align-items: center;
	height: 40px;
	background: ${({ theme }) => theme.menu.background};
	border-top-right-radius: 6px;
	border-top-left-radius: 6px;
	overflow: auto;

	div {
		padding: 0 10px;
		cursor: pointer;
		min-width: 55px;
	}
`;

const Tab = styled.div``;

const TabPanel = styled.div`
	padding: 10px 0;
	display: flex;
	flex-direction: column;
	gap: 10px;
	background: ${({ theme }) => theme.chat.conversationHeader};
	border-bottom-right-radius: 6px;
	border-bottom-left-radius: 6px;
	overflow: auto;
	max-height: calc(100% + 5rem);
`;

const ReactionUser = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: 0 8px;
	color: ${({ theme }) => theme.chat.receivedMessageTextColor};
`;

const Avatar = styled.div`
	display: flex;
	height: 42px;
	width: 42px;
	position: relative;
`;

const MyReaction = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-right: auto;
    cursor: pointer;

	span {
		padding-left: 12px;
	}

	span.removeMyReaction {
		font-size: 12px;
		color: ${({ theme }) => theme.chat.sentTime};
	}
`;

